import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./TopBar.module.css";

/**
 * Company product bar above the site header (Figma pNmVyXzNBlsKP42DQoUYSU).
 * The tagline on the right describes the hovered (or focused) product, this site's
 * by default. The ✕ collapses the bar for this page view only.
 *
 * This site's header renders its navbar twice — once in flow and once more with
 * Bootstrap's `position-fixed` pinned at `top: 0; z-index: 9999`. That copy would
 * cover a bar sitting in flow above it, so the bar publishes `--topbar-offset`
 * (how much of itself is still on screen) and the header consumes it.
 */

const CURRENT_PRODUCT = "globalSolutions";

/** globalpay.uz and global.uz both serve `ru` unprefixed and prefix every other locale. */
const localized = (origin, locale) => `${origin}${locale === "ru" ? "" : `/${locale}`}`;

const buildProducts = (locale) => [
  { key: "globalSolutions", name: "Global Solutions", href: "/" },
  { key: "globalPay", name: "Global Pay", href: localized("https://globalpay.uz", locale) },
  { key: "globalDocs", name: "Global Docs", href: "https://globaldocs.uz" },
  { key: "globalPartner", name: "Global Partner", href: "https://www.globalpartner.uz" },
];

export default function TopBar({ locale = "ru" }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [previewed, setPreviewed] = useState(null);

  // Keep the fixed navbar riding on top of the bar until the bar has scrolled away.
  useEffect(() => {
    const root = document.documentElement;
    const height = dismissed ? 0 : 43;
    const update = () => {
      const left = Math.max(0, height - window.pageYOffset);
      root.style.setProperty("--topbar-offset", `${left}px`);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [dismissed]);

  useEffect(
    () => () => document.documentElement.style.removeProperty("--topbar-offset"),
    []
  );

  const preview = (key, on) =>
    setPreviewed((current) => (on ? key : current === key ? null : current));

  const dismiss = () => {
    // The bar turns inert with this button in it: hand focus to the logo instead.
    const home = document.querySelector("[data-header-home]");
    if (home) home.focus({ preventScroll: true });
    setDismissed(true);
  };

  return (
    <div
      className={`${styles.topbar} ${dismissed ? styles.dismissed : ""}`}
      aria-hidden={dismissed || undefined}
    >
      <div className={styles.inner}>
        {/* From lg the tabs keep their width and only the tagline gives way. */}
        <nav aria-label={t("TopBar.navAria")}>
          <ul className={styles.products}>
            {buildProducts(locale).map((product) => {
              const current = product.key === CURRENT_PRODUCT;
              return (
                <li key={product.key}>
                  <a
                    href={product.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-current={current ? "page" : undefined}
                    tabIndex={dismissed ? -1 : undefined}
                    onMouseEnter={() => preview(product.key, true)}
                    onMouseLeave={() => preview(product.key, false)}
                    onFocus={() => preview(product.key, true)}
                    onBlur={() => preview(product.key, false)}
                    className={`${styles.product} ${current ? styles.productActive : ""}`}
                  >
                    {product.name}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <p className={styles.note}>
          <span className={styles.noteText}>
            {t(`TopBar.taglines.${previewed || CURRENT_PRODUCT}`)}
          </span>
          <button
            type="button"
            aria-label={t("TopBar.dismiss")}
            onClick={dismiss}
            tabIndex={dismissed ? -1 : undefined}
            className={styles.close}
          >
            <img src="/icons/topbar-close.svg" alt="" width={24} height={24} />
          </button>
        </p>
      </div>
    </div>
  );
}
